/**
 * Store registry and snapshot serialiser for the debug bridge.
 *
 * Exposes a flat map of store name -> Zustand `getState` accessor for the
 * shared cross-cutting stores plus the auth feature store. Per-feature stores
 * can be added opportunistically as the operator wants visibility into them.
 *
 * `snapshotStores()` returns a serialisable snapshot suitable for JSON
 * encoding. `serialise()` is a depth-limited replacer that handles cyclic
 * references and non-serialisable values (functions, classes) without
 * throwing.
 */

import { useAuthStore } from '@/features/auth'
import { useCustomersStore } from '@/features/customers'
import { useInboxStore } from '@/features/inbox-manager'
// Imported from the store MODULE, not the feature barrel, unlike its
// neighbours above. The Insights barrel re-exports all six page components;
// a static import of it here would pull the entire feature — charts and all —
// into the main chunk and undo the `lazy()` code-split in routes.tsx. The
// store itself is a standalone Zustand module with no component imports.
import { useInsightsStore } from '@/features/insights/store'
import { useSessionsStore } from '@/features/sessions'
import {
  useCommandPaletteStore,
  useConnectionStore,
  useEnvironmentStore,
  useModalStore,
  useModelStore,
  useSettingsStore,
  useShortcutStore,
  useThemeStore,
  useUIStore,
} from '@/shared/stores'

/** Maximum depth `serialise` will recurse before bailing with `<max-depth>`. */
const MAX_DEPTH = 6

/**
 * Registry of all stores the debug bridge will expose via `/debug/state`.
 *
 * Add new stores opportunistically — keep the registry conservative for now.
 */
export const STORE_REGISTRY: Record<string, () => unknown> = {
  auth: () => useAuthStore.getState(),
  customers: () => useCustomersStore.getState(),
  inbox: () => useInboxStore.getState(),
  // Insights' global filter bar (time range, granularity, app + country
  // filters). Exposed because every Insights panel is a pure function of it,
  // so a wrong-looking chart is almost always a wrong filter.
  insights: () => useInsightsStore.getState(),
  sessions: () => useSessionsStore.getState(),
  settings: () => useSettingsStore.getState(),
  environment: () => useEnvironmentStore.getState(),
  modal: () => useModalStore.getState(),
  connection: () => useConnectionStore.getState(),
  theme: () => useThemeStore.getState(),
  ui: () => useUIStore.getState(),
  model: () => useModelStore.getState(),
  shortcut: () => useShortcutStore.getState(),
  commandPalette: () => useCommandPaletteStore.getState(),
}

/**
 * Take a JSON-safe snapshot of every registered store. Per-store failures are
 * captured as `{ __error: string }` so a single broken store does not poison
 * the whole snapshot.
 */
export function snapshotStores(): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [name, get] of Object.entries(STORE_REGISTRY)) {
    try {
      out[name] = serialise(get())
    } catch (err) {
      out[name] = { __error: err instanceof Error ? err.message : String(err) }
    }
  }
  return out
}

/**
 * Depth-limited recursive serialiser that produces JSON-safe output:
 *   - Functions become the literal string `<function>`.
 *   - Cyclic references become the literal string `<circular>`.
 *   - At depth `MAX_DEPTH`, returns the literal string `<max-depth>`.
 *   - `Date` becomes its ISO string.
 *   - `Error` becomes `{ name, message, stack? }`.
 *   - All other plain objects and arrays are walked.
 */
export function serialise(
  value: unknown,
  depth: number = 0,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (depth >= MAX_DEPTH) return '<max-depth>'

  if (value === null || value === undefined) return value
  const t = typeof value
  if (t === 'string' || t === 'number' || t === 'boolean') return value
  if (t === 'bigint') return (value as bigint).toString()
  if (t === 'symbol') return value.toString()
  if (t === 'function') return '<function>'

  // Objects ── handle special types before generic walk.
  const obj = value as object
  if (seen.has(obj)) return '<circular>'
  seen.add(obj)

  if (value instanceof Date) return value.toISOString()
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      ...(value.stack ? { stack: value.stack } : {}),
    }
  }
  if (value instanceof Map) {
    const out: Record<string, unknown> = {}
    for (const [k, v] of value.entries()) {
      out[String(k)] = serialise(v, depth + 1, seen)
    }
    return { __map: out }
  }
  if (value instanceof Set) {
    return { __set: Array.from(value).map((v) => serialise(v, depth + 1, seen)) }
  }
  if (Array.isArray(value)) {
    return value.map((v) => serialise(v, depth + 1, seen))
  }

  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    out[k] = serialise(v, depth + 1, seen)
  }
  return out
}
